import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { DateTimePicker, DateTimePickerProps } from "@mui/x-date-pickers";
import { Controller, FieldValues, Path, useFormContext } from "react-hook-form";
import { Box, Stack, Text } from "@chakra-ui/react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

dayjs.extend(utc);
dayjs.extend(timezone);

const DatepickerFormInput = <FormValues extends FieldValues = any>({
  timezone,
  label = "",
  name,
  deps = [],
  validate = () => true,
  validationFailMessage,
  dateTimeProps = {},
  transform = {
    input: (val: Date) => {
      return val ? dayjs.utc(val) : null;
    },
    output: (val: Dayjs) => {
      return val?.isValid() ? val?.toISOString() : null;
    },
  },
  method = "onAccept",
  skipValidate = false,
  showTimezone = false,
  overrideError = undefined,
  placeholder = "MM/DD/YYYY hh:mm aa",
}: {
  timezone: string;
  name: Path<FormValues>;
  deps?: string[];
  label?: string;
  validate?: (val: Dayjs, otherValues: FormValues) => boolean | string;
  skipValidate?: boolean;
  showTimezone?: boolean;
  validationFailMessage?: string;
  dateTimeProps?: DateTimePickerProps<Dayjs>;
  transform?: { input: (val: any) => any; output: (val: any) => any };
  method?: "onAccept" | "onChange";
  overrideError?: string;
  placeholder?: string;
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>();

  const props = {
    [method]: (field) => ({
      [method]: (date) => field.onChange(transform.output(date)),
    }),
    // Produces output like onAccept={field.onChange()} or onChange={field.onChange}
  };

  const validationProps = skipValidate
    ? {}
    : {
        validate: (fieldToValidate, otherValues) => {
          try {
            if (fieldToValidate === null || fieldToValidate === undefined) {
              return `${name} is required.`;
            }

            const validationResult = validate(
              transform.input(fieldToValidate),
              otherValues
            );
            if (!validationResult) {
              return validationFailMessage || `Failed validating ${name}`;
            }

            return true;
          } catch (e) {
            return validationFailMessage || `Failed validating ${name}`;
          }
        },
      };

  return (
    <Stack w="full" spacing={0}>
      <Text fontWeight={"medium"}>{label}</Text>
      <Controller<FormValues>
        name={name}
        control={control}
        rules={{
          deps: deps as Path<FormValues>[],
          ...validationProps,
        }}
        render={({ field }) => {
          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                {...field}
                value={transform.input(field.value)}
                onChange={(date) => {
                  return field.onChange(transform.output(date));
                }}
                {...props[method](field)}
                timezone={timezone}
                closeOnSelect
                {...dateTimeProps}
                slotProps={{
                  ...(dateTimeProps?.slotProps || {}),
                  textField: {
                    ...(dateTimeProps?.slotProps?.textField || {}),
                    placeholder,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        height: "48px",
                        "& fieldset": {
                          border: "1px solid #E2E8F0",
                          borderRadius: "8px",
                          boxShadow: "0px 2px 6px rgba(0,0,0,0.035)",
                        },
                        "&:hover fieldset": {
                          borderColor: "#CBD5E0",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3182CE",
                        },
                        "& .MuiButtonBase-root": {
                          color: "rgba(0,0,0,0.4)",
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          );
        }}
      />
      {showTimezone ? (
        <Text fontSize="sm" color="gray.500">
          {timezone}
        </Text>
      ) : null}
      <Box h={4}>
        {overrideError ? (
          <Text fontSize={"xs"} color="red.300" lineHeight={4}>
            {overrideError}
          </Text>
        ) : errors[name] ? (
          <Text fontSize={"xs"} color="red.300" lineHeight={4}>
            {`${errors[name].message}`}
          </Text>
        ) : null}
      </Box>
    </Stack>
  );
};

export default DatepickerFormInput;
